import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5locales_tr_TR from "@amcharts/amcharts5/locales/tr_TR";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ThemeContext } from '../../../context/theme-context';
import NumberFormatter from '../../../formatters/NumberFormatter';
import DateFormatter from '../../../formatters/DateFormatter';
import moment from 'moment/moment';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

const LineChartTA = (props) => {// props: {data,averageValue,selectedSecurity,divId}
    const numberFormatter = new NumberFormatter;
    const dateFormatter = new DateFormatter();
    const [chartData, setChartData] = useState(null);
    const [theme, setTheme] = useState('dark'); // ['dark', 'light'
    const currentTheme = useContext(ThemeContext).theme;

    const selectedSecurityFromStore = useSelector((state) => state.security?.selectedSecurity);
    const [symbolCode, setSymbolCode] = useState((props.selectedSecurity && props.selectedSecurity.code) || selectedSecurityFromStore?.code);
    const [symbolName, setSymbolName] = useState((props.selectedSecurity && props.selectedSecurity.name) || selectedSecurityFromStore?.name);

    // Get the language code from redux store
    const { i18n } = useTranslation();
    const profileBasicDetail = useSelector(state => state.profile.basicDetail);
    const languageCode = profileBasicDetail.languagecode || i18n.language || 'tr-TR';

    useEffect(() => {
        setTheme(currentTheme);
        const source = props.selectedSecurity || selectedSecurityFromStore || {};
        setSymbolCode(source.code);
        setSymbolName(source.name);
    }, [currentTheme, props.selectedSecurity, selectedSecurityFromStore]);



    useLayoutEffect(() => {
        // Log the data we received to help with debugging
        console.log(`LineChartTA: Received ${props.data?.length || 0} data points`);

        // Verify data and return early if no valid data
        if (!props.data || props.data.length <= 0) {
            console.warn('LineChartTA: No data provided to chart');
            return;
        }

        // Log the first and last data points to verify date ranges
        if (props.data.length > 0) {
            const firstItem = props.data[0];
            const lastItem = props.data[props.data.length - 1];
            console.log('LineChartTA: First data point:', new Date(firstItem.date).toISOString(), firstItem);
            console.log('LineChartTA: Last data point:', new Date(lastItem.date).toISOString(), lastItem);
        }

        const source = props.selectedSecurity || selectedSecurityFromStore || {};
        setSymbolCode(source.code);
        setSymbolName(source.name);
        setTheme(currentTheme);
        let root = am5.Root.new('lineChart');
        root.setThemes([am5themes_Animated.new(root)]);

        // Dynamically set locale based on language code
        if (languageCode === 'en-US') {
            root.locale = am5locales_en_US;
            console.log("Using English locale for chart");
        } else {
            // Default to Turkish
            root.locale = am5locales_tr_TR;
            console.log("Using Turkish locale for chart");
        }

        var chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "panX",
                wheelY: "zoomX",
                paddingLeft: 30,
                paddingRight: 10,
                marginLeft: 10,
                marginRight: 10,
            })
        );

        // cursor tanımı daha sonra yapılacak

        var date = new Date();
        date.setHours(0, 0, 0, 0);
        var value = 100;

        function generateData() {
            value = Math.round((Math.random() * 10 - 5) + value);
            am5.time.add(date, "day", 1);
            return {
                date: date.getTime(),
                value: value
            };
        }

        function generateDatas(count) {
            var data = [];
            for (var i = 0; i < count; ++i) {
                data.push(generateData());
            }
            return data;
        }
        let axisRendererX = am5xy.AxisRendererX.new(root, {
            fill: theme === 'dark' ? am5.color('#626262') : am5.color('#231f20'),
        });

        axisRendererX.labels.template.setAll({
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231f20'),
            rotation: 0, // Initial rotation
            centerY: am5.p50, // Align the labels with the center
            centerX: am5.p50, // Center alignment
        });
        var xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                maxDeviation: 1,
                groupData: false,
                baseInterval: { timeUnit: "day", count: 1 }, // Use day as time unit
                // gridIntervals: [
                //     { timeUnit: "day", count: 2 },
                //     { timeUnit: "week", count: 1 },
                //     { timeUnit: "month", count: 10 },
                //     { timeUnit: "year", count: 365 }
                // ],
                renderer: axisRendererX,


                minGridDistance: 40, // Start with a reasonable minGridDistance
            })
        );


        xAxis.set("minorDateFormats", {
            day: "dd",
            month: "MM"
        });
        xAxis.get("renderer").labels.template.setAll({
            fontSize: 14,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            opacity: 1,
        });

        xAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 0,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
        });

        var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {
                pan: "zoom",
                opposite: true,

            })
        }));

        yAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 1,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            opposite: true,
        });

        yAxis.get("renderer").labels.template.setAll({
            // oversizedBehavior: "wrap",
            // maxWidth: 250,
            // textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            // opacity: 1,
        });

        // Create tooltip exactly like in PricePerformance
        var tooltipHTML = createTooltipHTML();

        let tooltipLineSeries = am5.Tooltip.new(root, {
            keepTargetHover: true,
            getFillFromSprite: true,
            getStrokeFromSprite: true,
            autoTextColor: true,
            getLabelFillFromSprite: true,
            fill: am5.color("#333"),
        });

        tooltipLineSeries.get("background").setAll({
            fill: am5.color("#333"),
            fillOpacity: 0,
            strokeOpacity: 0,
            getStrokeFromSprite: true,
        });
        tooltipLineSeries.label.set("fill", am5.color("#231f20"));

        var series = chart.series.push(am5xy.LineSeries.new(root, {
            name: "Series",
            xAxis: xAxis,
            stroke: am5.color('#42A5F5'),
            yAxis: yAxis,
            valueYField: "close",
            valueXField: "date",
            tooltip: tooltipLineSeries,
            tooltipHTML: tooltipHTML,
            sequencedInterpolation: true
        }));
        series.strokes.template.setAll({
            strokeWidth: 2,
        });

        // PricePerformance'taki gibi tooltip davranışını ayarla
        series.set("tooltip", tooltipLineSeries);

        series.bullets.push(function (root, series, dataItem) {
            let signalColor;

            try {
                // Safe check for dataItem and signal property
                if (dataItem && dataItem.dataContext && dataItem.dataContext.signal) {
                    const signal = String(dataItem.dataContext.signal);

                    if (signal.includes("negative")) {
                        signalColor = '#FF0000'; // Red for negative signal
                    }
                    else if (signal.includes("normal")) {
                        signalColor = '#bfc0c1'; // Gray for normal signal
                    }
                    else if (signal.includes("positive")) {
                        signalColor = '#00E300'; // Green for positive signal
                    }
                    else {
                        signalColor = series.get("fill"); // Default color
                    }
                } else {
                    signalColor = series.get("fill"); // Default color if no signal data
                }
            } catch (err) {
                console.error("Error determining bullet color:", err);
                signalColor = series.get("fill"); // Default color on error
            }

            let circle = am5.Circle.new(root, {
                radius: 5,
                fill: signalColor
            });

            let bullet = am5.Bullet.new(root, {
                sprite: circle
            });

            return bullet;
        });

        // Add cursor like in PricePerformance
        chart.set("cursor", am5xy.XYCursor.new(root, {
            behavior: "none"
        }));

        // Snap cursor to series
        series.set("snapTooltip", true);

        // Add scrollbar (place it under the chart)
        // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/
        let scrollbarX = am5.Scrollbar.new(root, {
            orientation: "horizontal",
            height: 10
        });
        chart.set("scrollbarX", scrollbarX);
        // Ensure it sits below x-axis labels at the very bottom
        chart.bottomAxesContainer.children.push(scrollbarX);
        scrollbarX.setAll({
            marginTop: 8,
            marginBottom: 0
        });

        try {
            // Set the chart data from filtered props.data
            console.log(`Setting chart data with ${props.data.length} points`);

            series.data.setAll(props.data);

            // Configure chart appearance animations
            series.appear(1000);
            chart.appear(1000, 100);

        } catch (err) {
            console.error("Error setting chart data:", err);
        }

        return () => {
            root.dispose();
        };
    }, [theme, props.data, languageCode]); // Added languageCode as dependency

    const createTooltipHTML = () => {
        // Get translated strings based on current language
        const dateLabel = languageCode === 'en-US' ? 'Date' : 'Tarih';
        const priceLabel = languageCode === 'en-US' ? 'Price' : 'Fiyat';
        const volumeLabel = languageCode === 'en-US' ? 'Volume' : 'Hacim';

        // Theme-based colors for a more modern look with increased transparency
        const backgroundColor = theme === 'dark' ? 'rgba(44, 44, 44, 0.6)' : 'rgba(255, 255, 255, 0.6)';
        const textColor = theme === 'dark' ? '#ffffff' : '#231f20';
        const nameColor = theme === 'dark' ? '#2fbc99' : '#2fbc99';
        const dateColor = theme === 'dark' ? '#2fbc99' : '#2fbc99';
        const labelColor = theme === 'dark' ? '#b0b0b0' : '#757575';
        const borderColor = theme === 'dark' ? 'rgba(68, 68, 68, 0.4)' : 'rgba(228, 228, 228, 0.4)';
        const boxShadow = theme === 'dark' ? '0 3px 6px rgba(0,0,0,0.2)' : '0 3px 6px rgba(0,0,0,0.07)';
        const priceDotColor = '#2FBC99';
        const volumeDotColor = '#42A5F5';

        return `
        <div style="padding: 5px 5px; background-color: ${backgroundColor}; color: ${textColor}; border-radius: 8px; border: 1px solid ${borderColor}; box-shadow: ${boxShadow}; font-family: 'Roboto-Medium', sans-serif; min-width: 120px; backdrop-filter: blur(3px);">
            <div style="font-size: 16px; font-weight: 500; margin-bottom: 1px;">
                ${symbolCode} - <span style="color: ${nameColor};">${symbolName}</span>
            </div>
            <div style="font-size: 13px; color: ${dateColor}; margin-bottom: 2px;">
                ${dateLabel} - {date.formatDate('dd/MM/yyyy')}
            </div>
            <div style="display: flex; align-items: center; font-size: 13px; margin-bottom: 2px;">
                <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${priceDotColor}; margin-right: 8px;"></span>
                <span style="color: ${labelColor}; margin-right: 0px;">${priceLabel}:</span>
                <span style="font-weight: 500;">{valueY.formatNumber('#,###.00')} ₺</span>
            </div>
   
        </div>`;
    }

    return (
        <div className='index-analysis-line-chart'>
            <div id='lineChart' style={{ width: "100%", height: "375px" }}></div>
        </div>
    )
}

export default LineChartTA;
